import { useState } from 'react';
import { Button, Card, CardContent } from '../../components/ui';
import { useLanguageStore } from '../../store/languageStore';
import { getTranslation } from '../../i18n/translations';

export const ContactPage = () => {
  const { language } = useLanguageStore();
  const t = getTranslation(language);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', message: '' });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const contactMethods = [
    {
      icon: '📧',
      title: t.contact.methods.email.title,
      description: t.contact.methods.email.description,
      value: 'contact@squadronplay.com',
    },
    {
      icon: '💬',
      title: t.contact.methods.support.title,
      description: t.contact.methods.support.description,
      value: t.contact.methods.support.value,
    },
    {
      icon: '🌐',
      title: t.contact.methods.community.title,
      description: t.contact.methods.community.description,
      value: t.contact.methods.community.value,
    },
  ];

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Hero Section */}
      <section className="mb-16 text-center">
        <h1 className="mb-4 text-5xl font-bold md:text-6xl lg:text-7xl">
          <span className="bg-gradient-to-r from-primary via-purple-500 to-pink-500 bg-clip-text text-transparent">
            {t.contact.hero.title}
          </span>
        </h1>
        <p className="mx-auto max-w-2xl text-lg text-muted-foreground md:text-xl">
          {t.contact.hero.tagline}
        </p>
      </section>

      {/* Contact Methods Section */}
      <section className="mb-16">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-3xl font-bold">{t.contact.ways.title}</h2>
          <p className="text-muted-foreground">{t.contact.ways.subtitle}</p>
        </div>
        
        <div className="grid gap-6 md:grid-cols-3">
          {contactMethods.map((method, index) => (
            <Card key={index} className="border-2 transition-all hover:border-primary/50 hover:shadow-lg">
              <CardContent className="pt-6 text-center">
                <div className="mb-4 text-4xl">{method.icon}</div>
                <h3 className="mb-2 text-xl font-bold">{method.title}</h3>
                <p className="mb-2 text-sm text-muted-foreground">{method.description}</p>
                <p className="font-semibold text-primary">{method.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <h2 className="mb-2 text-3xl font-bold">{t.contact.form.title}</h2>
          <p className="text-muted-foreground">{t.contact.form.subtitle}</p>
        </div>

        <Card className="border-2">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="mb-2 block text-sm font-medium">
                  {t.contact.form.name}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border-2 border-border bg-background px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder={t.contact.form.namePlaceholder}
                />
              </div>

              <div>
                <label htmlFor="email" className="mb-2 block text-sm font-medium">
                  {t.contact.form.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full rounded-md border-2 border-border bg-background px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder={t.contact.form.emailPlaceholder}
                />
              </div>

              <div>
                <label htmlFor="message" className="mb-2 block text-sm font-medium">
                  {t.contact.form.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="w-full rounded-md border-2 border-border bg-background px-4 py-3 transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder={t.contact.form.messagePlaceholder}
                />
              </div>

              <Button type="submit" className="w-full py-6 text-lg">
                {t.contact.form.submit}
              </Button>
            </form>
          </CardContent>
        </Card>
      </section>
    </div>
  );
};
